import React, {useEffect, useMemo} from "react";
import {useNavigate} from "react-router-dom";

import {useDebugMode} from "../hooks/useDebugMode";
import SectionHeading from "./SectionHeading";
import StoryCard from "./StoryCard";
import RecommendationWhyBadge from "./RecommendationWhyBadge";
import {Skeleton} from "./ui/skeleton";

/**
 * The reader's own front page.
 *
 * The rails further down are horizontal and equal-weighted, which is right for
 * comparing surfaces but wrong for the top of a newspaper: the fold needs a
 * splash. This renders the personalised ranking in broadsheet form - the
 * top-ranked story takes the lead position with a standfirst and a large
 * picture, the next four report in beside it - so the first thing a visitor
 * sees on any device is already personalised, rather than a control feed they
 * have to scroll past.
 */
const PersonalisedFrontPage = ({
  contextId,
  fetchRecommendationsForContext,
  data = [],
  isLoading = false,
  viewInterceptor,
}) => {
  const navigate = useNavigate();
  const debugMode = useDebugMode();

  useEffect(() => {
    fetchRecommendationsForContext(contextId ?? "");
  }, [contextId, fetchRecommendationsForContext]);

  const stories = useMemo(
    () =>
      (data || [])
        .map((entry) => {
          const metadata = entry?.metadata;
          if (!metadata) return null;
          return {
            id: entry.id,
            title: metadata.originalTitle || entry.name || "Untitled",
            summary: metadata.summary || entry.description || "",
            image: metadata.image || "",
            category: metadata.aiCategorization || metadata.topic || "",
            source: metadata.source || "",
            timeToRead: metadata.timeToRead || "",
            match:
              typeof entry.baseScore === "number"
                ? Math.round(entry.baseScore * 100)
                : null,
            explanation: entry.recommendationExplanation || null,
            recSource: entry.recommendationSource || "",
            raw: {
              uuid: entry.id,
              originalTitle: metadata.originalTitle || entry.name,
              aiSummary: metadata.summary || entry.description,
              aiHtmlDescription: metadata.aiHtmlDescription,
              aiCategorization: metadata.aiCategorization || metadata.topic,
              image: metadata.image,
              source: metadata.source,
              link: metadata.link,
              timeToRead: metadata.timeToRead,
            },
          };
        })
        .filter(Boolean)
        .slice(0, 5),
    [data]
  );

  const open = (story) => {
    if (!story?.id) return;
    try {
      sessionStorage.setItem(
        `neuron:article:${story.id}`,
        JSON.stringify(story.raw)
      );
    } catch {
      // best effort
    }
    navigate(`/article/${story.id}`);
  };

  const heading = (
    <SectionHeading
      kicker="Your front page"
      title="Ranked for you"
      standfirst="Chosen for you in real time. Read a few and reload to watch it move."
      debugMode={debugMode}
      debugNote="ctx 2323 · model order, no pins or boosts · filter type!=comment"
    />
  );

  if (isLoading) {
    return (
      <section className="container py-8">
        {heading}
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1.75fr)_minmax(0,1fr)] lg:gap-10">
          <div className="flex flex-col gap-3">
            <Skeleton className="aspect-[16/9] w-full" />
            <Skeleton className="h-9 w-4/5" />
            <Skeleton className="h-4 w-full" />
          </div>
          <div className="flex flex-col gap-5">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex gap-3">
                <div className="flex flex-1 flex-col gap-2">
                  <Skeleton className="h-3 w-1/3" />
                  <Skeleton className="h-5 w-full" />
                </div>
                <Skeleton className="size-[84px] shrink-0" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!stories.length) {
    return (
      <section className="container py-8">
        {heading}
        <div className="border border-dashed border-rule-strong px-6 py-12 text-center">
          <p className="font-serif text-h3 font-bold text-ink">
            Your front page is still being set
          </p>
          <p className="mx-auto mt-1.5 max-w-md text-body text-ink-tertiary">
            Open a couple of stories and the engine will start ranking the front
            page around you.
          </p>
        </div>
      </section>
    );
  }

  const [lead, ...rest] = stories;

  return (
    <section className="container py-8">
      {heading}

      <div className="grid gap-8 lg:grid-cols-[minmax(0,1.75fr)_minmax(0,1fr)] lg:gap-10">
        <div className="relative">
          <StoryCard
            id={lead.id}
            ref={viewInterceptor?.getItemRef(lead.id)}
            title={lead.title}
            description={lead.summary}
            image={lead.image}
            category={lead.category}
            duration={lead.timeToRead}
            sources={[lead.source]}
            variant="lead"
            onAssetClick={() => open(lead)}
          >
            {lead.match != null ? (
              <span className="font-sans text-meta font-semibold tabular-nums text-pencil">
                {lead.match}% match
              </span>
            ) : null}
          </StoryCard>
          <RecommendationWhyBadge
            explanation={lead.explanation}
            source={lead.recSource}
            className="absolute left-2 top-2"
          />
        </div>

        {rest.length ? (
          <div className="flex flex-col divide-y divide-rule border-t border-rule lg:border-t-0">
            {rest.map((story) => (
              <div key={story.id} className="py-4 first:pt-0">
                <StoryCard
                  id={story.id}
                  ref={viewInterceptor?.getItemRef(story.id)}
                  title={story.title}
                  description={story.summary}
                  image={story.image}
                  category={story.category}
                  duration={story.timeToRead}
                  sources={[story.source]}
                  variant="compact"
                  onAssetClick={() => open(story)}
                >
                  {story.match != null ? (
                    <span className="font-sans text-meta font-semibold tabular-nums text-pencil">
                      {story.match}% match
                    </span>
                  ) : null}
                </StoryCard>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default PersonalisedFrontPage;
